import React, { useState, useMemo, useEffect } from "react";
import { useOrder } from "../context/OrderContext";
import { useOrderSave } from "../context/OrderSaveContext";
import TablerLayout from "../components/layout/TablerLayout";
import StepperNavButton from "../components/stepper/StepperNavButton";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { showErrorToast, showSuccessToast} from "../components/toasts/ToastService";
import { useStaticOptions } from "../context/StaticOptionsContext";
import Modal from "../components/modals/Modal";
import { IconExclamationMark } from "@tabler/icons-react";

const BetetTipus = () => {
    const { order, updateOrder } = useOrder();
    const { saveOrder } = useOrderSave();
    const navigate = useStepNavigate();
    const { staticOptions} = useStaticOptions();
    const foot_liners = staticOptions?.foot_liners || [];

    const normalizeValue = (value) =>
        (value || "").trim().toLocaleLowerCase("hu-HU");

    const modalTriggerTokens = ["ttt", "j-10", "j10", "j-15", "j15", "egyedi lábágy", "tc-betét", "tc", "egyedi labagy", "egyedi lábágy", "egylá", "egyedi lábagy", "egyedi lábágy készítése", "egyedi lábágy készítése", "egyedi labagy készítése", "egyedi lábágy keszítése", "egyedi labagy keszítése"];

    const isTriggeredName = (value) => {
        const n = normalizeValue(value);
        return modalTriggerTokens.some((token) => n.includes(token));
    };

    const findFootLinerByName = (name) => {
        const n = normalizeValue(name);
        if (!n) return null;

        const exact = foot_liners.find(
            (item) => normalizeValue(item.foot_liner_name) === n
        );
        if (exact) return exact;

        const fuzzy = foot_liners.find((item) => {
            const linerName = normalizeValue(item.foot_liner_name);
            return n.includes(linerName) || linerName.includes(n);
        });

        return fuzzy || null;
    };

    const findFootLinerByCode = (aidfinance_code) => {
        const code = (aidfinance_code || "").trim().toLocaleLowerCase("hu-HU");
        if (!code) return null;

        // Keresünk olyan foot_linert, amelynek a name-je tartalmazza vagy egyenlő a kóddal
        const matched = foot_liners.find((item) => {
            const linerName = normalizeValue(item.foot_liner_name);
            return linerName === code || linerName.includes(code);
        });

        return matched || null;
    };

    const [insole_type_left_obj, setBal] = useState(() => {
        const szam = order.hmeliorder?.hmeliorder_insole_type_left_id || 1;
        const name = order.hmeliorder?.hmeliorder_insole_type_left || "";
        return { szam, name };
    });
    const [insole_type_right_obj, setJobb] = useState(() => {
        const szam = order.hmeliorder?.hmeliorder_insole_type_right_id || 1;
        const name = order.hmeliorder?.hmeliorder_insole_type_right || "";
        return { szam, name };
    });
    const [loading, setLoading] = useState(false);
    const [showWarningModal, setShowWarningModal] = useState(false);

    const { preselectedLeft, preselectedRight, lockedLeft, lockedRight } = useMemo(() => {
        const result = {
            preselectedLeft: null,
            preselectedRight: null,
            lockedLeft: false,
            lockedRight: false,
        };

        if (!Array.isArray(order.venyek) || foot_liners.length === 0) {
            return result;
        }

        order.venyek.forEach((veny) => {
            if (!Array.isArray(veny?.components)) return;

            veny.components.forEach((component) => {
                // Első: próbálj a name alapján keresni
                const matched = findFootLinerByName(component?.aidfinance_national);

                // Ha nem találtál name alapján, próbálj a code alapján keresni
                const matchedByCode = !matched ? findFootLinerByCode(component?.aidfinance_code) : null;

                const finalMatched = matched || matchedByCode;

                if (!finalMatched) return;

                const mapped = {
                    szam: finalMatched.foot_liner_szam,
                    name: finalMatched.foot_liner_name,
                };

                if (component?.is_left === 1) {
                    if (!result.preselectedLeft) result.preselectedLeft = mapped;
                    result.lockedLeft = true;
                    return;
                }

                if (component?.is_left === 0) {
                    if (!result.preselectedRight) result.preselectedRight = mapped;
                    result.lockedRight = true;
                    return;
                }

                if (!result.preselectedLeft) result.preselectedLeft = mapped;
                if (!result.preselectedRight) result.preselectedRight = mapped;
                result.lockedLeft = true;
                result.lockedRight = true;
            });
        });

        return result;
    }, [order.venyek, foot_liners]);

    useEffect(() => {
        let leftToSet = insole_type_left_obj;
        let rightToSet = insole_type_right_obj;
        let changed = false;

        if (lockedLeft && preselectedLeft) {
            if (
                insole_type_left_obj?.szam !== preselectedLeft.szam ||
                normalizeValue(insole_type_left_obj?.name) !== normalizeValue(preselectedLeft.name)
            ) {
                leftToSet = preselectedLeft;
                setBal(preselectedLeft);
                changed = true;
            }
        }

        if (lockedRight && preselectedRight) {
            if (
                insole_type_right_obj?.szam !== preselectedRight.szam ||
                normalizeValue(insole_type_right_obj?.name) !== normalizeValue(preselectedRight.name)
            ) {
                rightToSet = preselectedRight;
                setJobb(preselectedRight);
                changed = true;
            }
        }

        if (!changed) return;

        updateOrder({
            ...order,
            hmeliorder: {
                ...order.hmeliorder,
                hmeliorder_insole_type_left: leftToSet?.name || "",
                hmeliorder_insole_type_left_id: leftToSet?.szam || null,
                hmeliorder_insole_type_right: rightToSet?.name || "",
                hmeliorder_insole_type_right_id: rightToSet?.szam || null,
            },
        });
    }, [
        lockedLeft,
        lockedRight,
        preselectedLeft,
        preselectedRight,
        insole_type_left_obj,
        insole_type_right_obj,
        order,
        updateOrder,
    ]);

    const hasTriggeredVenyComponent =
        Array.isArray(order.venyek) &&
        order.venyek.some(
            (veny) =>
                Array.isArray(veny?.components) &&
                veny.components.some((component) =>
                    isTriggeredName(component?.aidfinance_national)
                )
        );

    const hasTriggeredInsoleType = [
        insole_type_left_obj?.name,
        insole_type_right_obj?.name,
    ].some((name) => isTriggeredName(name));

    const shouldShowWarningModal = hasTriggeredVenyComponent || hasTriggeredInsoleType;

    const onefoot = Number(order.hmeliorder?.hmeliorder_is_onefoot_meas ?? 0);
    const isLeft = Number(order.hmeliorder?.hmeliorder_is_left ?? 0);

    const showBal = onefoot === 0 || (onefoot === 1 && isLeft === 1);
    const showJobb = onefoot === 0 || (onefoot === 1 && isLeft === 0);

    const handleSelect = (side, selected_obj) => {
        if (side === "bal" && lockedLeft) return;
        if (side === "jobb" && lockedRight) return;

        const formatted = {
            szam: selected_obj.foot_liner_szam,
            name: selected_obj.foot_liner_name,
        };

        if (side === "bal") {
            setBal(formatted);
            updateOrder({
                ...order,
                hmeliorder: {
                    ...order.hmeliorder,
                    hmeliorder_insole_type_left: selected_obj.foot_liner_name,
                    hmeliorder_insole_type_left_id: selected_obj.foot_liner_szam,
                },
            });
        } else {
            setJobb(formatted);
            updateOrder({
                ...order,
                hmeliorder: {
                    ...order.hmeliorder,
                    hmeliorder_insole_type_right: selected_obj.foot_liner_name,
                    hmeliorder_insole_type_right_id: selected_obj.foot_liner_szam,
                },
            });
        }
    };

    const handleNext = async () => {
        try {
            setLoading(true);

            const updatedOrder = {
                ...order,
                hmeliorder: {
                    ...order.hmeliorder,
                    hmeliorder_insole_type_right_id: insole_type_right_obj.szam,
                    hmeliorder_insole_type_right: insole_type_right_obj.name,
                    hmeliorder_insole_type_left_id: insole_type_left_obj.szam,
                    hmeliorder_insole_type_left: insole_type_left_obj.name,
                },
            };

            updateOrder(updatedOrder);

            const result = await saveOrder(updatedOrder);
            if (result.success) {
                showSuccessToast("A rendelés sikeresen mentve!");
                navigate("/CsatolandoMedia");
            } else {
                showErrorToast("Hiba történt a rendelés mentésekor!");
            }
        } catch (error) {
            console.error("Hiba a rendelés mentésekor:", error);
            showErrorToast("Hiba történt a rendelés mentésekor!");
        } finally {
            setLoading(false);
        }
    };

    const leftFilled = (insole_type_left_obj?.name ?? "").trim().length > 0;
    const rightFilled = (insole_type_right_obj?.name ?? "").trim().length > 0;
    const canProceed = (showBal ? leftFilled : true) && (showJobb ? rightFilled : true);

    const handleContinue = () => {
        if (shouldShowWarningModal) {
            setShowWarningModal(true);
            return;
        }

        handleNext();
    };

    return (
        <TablerLayout title="Milyen betét típust szeretne?">
            <div className="row justify-content-center">
                <div className="col-md-10">
                    <div className="card-body">
                        <div className="row g-4">
                            {showBal && (
                                <div className={showJobb ? "col-12 col-md-6 d-flex flex-column align-items-center" : "col-12 col-md-8 mx-auto d-flex flex-column align-items-center"}>
                                    <div className="text-center fw-bold fs-5 mb-3">
                                        Bal láb {lockedLeft ? "(vény alapján rögzítve)" : ""}
                                    </div>
                                    {foot_liners.map((betet) => (
                                        <button
                                            key={betet.foot_liner_szam}
                                            type="button"
                                            className={`btn w-100 mb-3 py-4 px-3 shadow-sm rounded-4 d-flex flex-column align-items-center fw-bold${
                                                insole_type_left_obj.szam === betet.foot_liner_szam
                                                    ? " btn-primary"
                                                    : " btn-outline-primary"
                                            }`}
                                            onClick={() => handleSelect("bal", betet)}
                                            disabled={lockedLeft}
                                        >
                                            {betet.foot_liner_name}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {showJobb && (
                                <div className={showBal ? "col-12 col-md-6 d-flex flex-column align-items-center" : "col-12 col-md-8 mx-auto d-flex flex-column align-items-center"}>
                                    <div className="text-center fw-bold fs-5 mb-3">
                                        Jobb láb {lockedRight ? "(vény alapján rögzítve)" : ""}
                                    </div>
                                    {foot_liners.map((betet) => (
                                        <button
                                            key={betet.foot_liner_szam}
                                            type="button"
                                            className={`btn w-100 mb-3 py-4 px-3 shadow-sm rounded-4 d-flex flex-column align-items-center fw-bold${
                                                insole_type_right_obj.szam === betet.foot_liner_szam
                                                    ? " btn-primary"
                                                    : " btn-outline-primary"
                                            }`}
                                            onClick={() => handleSelect("jobb", betet)}
                                            disabled={lockedRight}
                                        >
                                            {betet.foot_liner_name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="card-footer d-flex justify-content-between">
                        <StepperNavButton
                            side="left"
                            label="Vissza"
                            onClick={() => navigate("/Gordulo")}
                        />
                        <StepperNavButton
                            side="right"
                            label="Tovább"
                            onClick={handleContinue}
                            disabled={!canProceed || loading}
                        />
                    </div>
                </div>
            </div>

            <Modal
                isOpen={showWarningModal}
                onClose={() => setShowWarningModal(false)}
                onConfirm={handleNext}
                children="Megtörtént a léptetőhabos méretvétel?"
                title="Méretvétel"
                cancelText="Nem"
                cancelClass="btn btn-warning"
                confirmText="Igen, megtörtént!"
                confirmClass="btn btn-primary"
                showCancel={true}
                showConfirm={true}
                showClose={false}
                confirmDisabled={loading}
                icon={<IconExclamationMark size={70} stroke={2} className="text-warning" />}
            >
                <h4>Megtörtént a léptetőhabos méretvétel?</h4>
            </Modal>
        </TablerLayout>
    );
};

export default BetetTipus;